import { PriorityHigh, Search } from '@mui/icons-material';
import {
  CircularProgress,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import { Box } from '@mui/system';
import type { ChangeEvent, FC } from 'react';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { usePopover } from 'src/hooks/use-popover';
import { DropDownIcon } from 'src/pages/therapist-dashboard/dashboard-icon';
import { selectPatient } from 'src/redux/actions/patientAction';
import { getTherapyPlanningPatient_2_0 } from 'src/redux/actions/therapyPlanning';
import { patientRowTestId, therapyPlanningTestId } from './test-ids';

interface TherapyPlanningPatientList {
  selectedPatient: any;
  setSearchTerm: any;
  searchTerm: any;
  setCurrentIndex: any;
  selectedDisciplines?: string[];
}

export const TherapyPlanningPatientList: FC<TherapyPlanningPatientList> = (props) => {
  const {
    searchTerm,
    setSearchTerm,
    selectedPatient,
    setCurrentIndex,
    selectedDisciplines,
    ...other
  } = props;
  const popover = usePopover<HTMLButtonElement>();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const inputRef = useRef<HTMLInputElement>(null);
  const patientResponse_2_0 = useSelector(
    (state: any) => state.getTherapyPlanningPatientReducer_2_0
  );

  const handleValueChange = (option: any, index: number): void => {
    // setSelectedIndex(index);
    // if (index === 0) {
    //   dispatch(selectPatient(option));
    // }
    setCurrentIndex(index);
    navigate('/scheduling/therapy-planning/details/info');
    // setSelectedValues(option);
    dispatch(selectPatient(patientResponse_2_0?.totalPatient[index]));
    if (searchTerm) {
      setSearchTerm('');
    }
    popover.handleClose();
  };

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value);
  };

  const handleOpenPopover = () => {
    popover.handleOpen();
  };

  const avoidBubblingUp = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10 && !patientResponse_2_0?.loading) {
      if (
        patientResponse_2_0?.response?.data?.totalElements !==
        patientResponse_2_0?.totalPatient?.length
      ) {
        dispatch(
          getTherapyPlanningPatient_2_0({
            params: {
              search: '',
              page: patientResponse_2_0?.pageNumber,
              pageSize: 20,
              ...(selectedDisciplines?.length && {
                activeDisciplines: selectedDisciplines.join(','),
              }),
            },
            isFirstTimeCall: false,
          })
        );
      }
    }
  };

  const patientsWithPlanningRequired =
    patientResponse_2_0?.totalPatient?.filter(
      (p: any) => p.therapyPlanningStatus === 'REQUIRES PLANNING'
    ) || [];

  const otherPatients =
    patientResponse_2_0?.totalPatient?.filter(
      (p: any) => p.therapyPlanningStatus !== 'REQUIRES PLANNING'
    ) || [];

  return (
    <>
      <Button
        onClick={handleOpenPopover}
        ref={popover.anchorRef}
        data-testid={therapyPlanningTestId('patient-picker')}
        {...other}
        sx={{
          ml: 2,
          borderColor: '#afb3ba9e',
          color: '#111927',
          pr: 1.5,
          pl: 2,
          fontSize: '14px',
          fontWeight: 500,
          height: '36px',
          display: 'flex',

          justifyContent: 'start',
          '&:hover': {
            color: '#111927',
            borderColor: '#afb3ba9e',
          },
        }}
        variant="outlined"
        endIcon={<DropDownIcon />}
      >
        {`${selectedPatient?.lastName || ''} ${selectedPatient?.firstName || ''}`}
      </Button>

      <Menu
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
        PaperProps={{
          sx: {
            width: 250,
            '& .MuiMenuItem-root + .MuiDivider-root': {
              marginTop: '0px',
            },
          },
        }}
      >
        <TextField
          variant="outlined"
          placeholder={`Search by Patient`}
          fullWidth
          onChange={handleSearchInputChange}
          value={searchTerm}
          inputProps={{ 'data-testid': therapyPlanningTestId('patient-search') }}
          sx={{
            p: 1,
            pt: 0,
            '& .MuiInputBase-input': {
              padding: '12px 0px',
            },
          }}
          inputRef={inputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          onKeyDown={avoidBubblingUp}
        />

        <Box
          ref={containerRef}
          onScroll={handleScroll}
          style={{
            height: '400px',
            overflowY: 'auto',
          }}
        >
          {!patientResponse_2_0?.totalPatient?.length && !patientResponse_2_0?.loading ? (
            <Typography
              color={'text.secondary'}
              variant="subtitle2"
              textAlign={'center'}
              mt={2}
              data-testid={therapyPlanningTestId('patient-search-empty')}
            >
              No results found
            </Typography>
          ) : (
            <>
              {patientsWithPlanningRequired.length > 0 && (
                <>
                  {' '}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mt={1}
                      mb={0.5}
                      pl={1.5}
                    >
                      Planning Required
                    </Typography>
                    <Tooltip
                      placement="top"
                      title="Planning required"
                    >
                      <PriorityHigh
                        sx={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          backgroundColor: '#F79009',
                          padding: '1px',
                          color: '#fff',
                          marginLeft: '8px',
                          cursor: 'pointer',
                        }}
                      />
                    </Tooltip>
                  </Box>
                  {patientsWithPlanningRequired.map((option: any, index: number) => (
                    <MenuItem
                      onClick={() => handleValueChange(option, index)}
                      key={option?.uuid}
                      data-testid={patientRowTestId(option?.uuid)}
                      sx={{
                        overflow: 'hidden',
                        pl: 3,
                        backgroundColor:
                          selectedPatient?.uuid === option?.uuid
                            ? 'rgb(206 207 255 / 50%)'
                            : 'none',
                      }}
                      title={`${option?.lastName}, ${option?.firstName}`}
                    >
                      {`${option?.lastName}, ${option?.firstName}`}
                    </MenuItem>
                  ))}
                </>
              )}

              {otherPatients.length > 0 && (
                <>
                  {patientsWithPlanningRequired.length ? (
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mt={2}
                      mb={0.5}
                      pl={1.5}
                    >
                      Other Patients
                    </Typography>
                  ) : null}
                  {otherPatients.map((option: any, index: number) => (
                    <MenuItem
                      onClick={() =>
                        handleValueChange(option, patientsWithPlanningRequired?.length + index)
                      }
                      key={option?.uuid}
                      data-testid={patientRowTestId(option?.uuid)}
                      sx={{
                        overflow: 'hidden',
                        display: 'flex',
                        pl: 3,
                        justifyContent: 'space-between',
                        backgroundColor:
                          selectedPatient?.uuid === option?.uuid
                            ? 'rgb(206 207 255 / 50%)'
                            : 'none',
                      }}
                      title={`${option?.lastName}, ${option?.firstName}`}
                    >
                      {`${option?.lastName}, ${option?.firstName}`}
                    </MenuItem>
                  ))}
                </>
              )}
            </>
          )}

          {patientResponse_2_0?.loading && (
            <Box
              display="flex"
              justifyContent="center"
              p={2}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Menu>
    </>
  );
};
